import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { ArrowLeft, Users, MapPin, ChevronRight, X } from 'lucide-react';
import MapView from '@/components/MapView';
import { useNavigate } from 'react-router-dom';
import { useFreightSocket } from '@/hooks/useFreightSocket';

export default function AdminFleetMap() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  const { lastEvent } = useFreightSocket(token, user);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/analytics/dashboard');
      setDrivers(res.data.active_drivers || []);
    } catch (err) {
      console.error('Failed to fetch fleet data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on mount + poll every 30s as fallback
  useEffect(() => {
    if (!token) return;
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [token, fetchData]);

  // Re-fetch immediately on real-time location updates
  useEffect(() => {
    if (lastEvent?.type === 'location_update') {
      fetchData();
    }
  }, [lastEvent, fetchData]);

  // Build markers from driver data
  const markers = useMemo(() => {
    const items = [];
    const filteredDrivers = selectedDriverId
      ? drivers.filter(d => d.user_id === selectedDriverId)
      : drivers;

    filteredDrivers.forEach(driver => {
      // Driver truck marker
      if (driver.last_location?.lat && driver.last_location?.lng) {
        items.push({
          id: `truck-${driver.user_id}`,
          lat: driver.last_location.lat,
          lng: driver.last_location.lng,
          label: driver.name,
          type: 'truck',
          color: '#E5A100',
        });
      }

      // Pickup and delivery markers for each active load
      (driver.active_loads || []).forEach(load => {
        if (load.pickup_lat && load.pickup_lng) {
          items.push({
            id: `pickup-${load.load_id}`,
            lat: load.pickup_lat,
            lng: load.pickup_lng,
            label: load.pickup_name || 'Pickup',
            type: 'pickup',
            detail: load.cargo_description,
          });
        }
        if (load.delivery_lat && load.delivery_lng) {
          items.push({
            id: `delivery-${load.load_id}`,
            lat: load.delivery_lat,
            lng: load.delivery_lng,
            label: load.delivery_name || 'Delivery',
            type: 'delivery',
            detail: load.cargo_description,
          });
        }
      });
    });

    return items;
  }, [drivers, selectedDriverId]);

  // Build load route lines
  const routeLines = useMemo(() => {
    const filteredDrivers = selectedDriverId
      ? drivers.filter(d => d.user_id === selectedDriverId)
      : drivers;

    const lines = [];
    filteredDrivers.forEach(driver => {
      (driver.active_loads || []).forEach(load => {
        if (load.pickup_lat && load.delivery_lat) {
          lines.push({
            load_id: load.load_id,
            pickup_lat: load.pickup_lat,
            pickup_lng: load.pickup_lng,
            delivery_lat: load.delivery_lat,
            delivery_lng: load.delivery_lng,
            driver_lat: driver.last_location?.lat,
            driver_lng: driver.last_location?.lng,
          });
        }
      });
    });
    return lines;
  }, [drivers, selectedDriverId]);

  const handleBack = () => {
    if (window.opener) {
      window.close();
    } else {
      navigate('/admin');
    }
  };

  const handleSelectDriver = (driverId) => {
    setSelectedDriverId(driverId);
  };

  const handleShowAll = () => {
    setSelectedDriverId(null);
  };

  const selectedDriver = selectedDriverId
    ? drivers.find(d => d.user_id === selectedDriverId)
    : null;

  return (
    <div className="h-screen w-screen flex bg-surface text-foreground overflow-hidden">
      {/* Map area */}
      <div className="flex-1 relative">
        {/* Top bar overlay */}
        <div className="absolute top-0 left-0 right-0 z-[1000] flex items-center gap-3 p-3">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 px-3 py-2 bg-surface/90 backdrop-blur border border-input rounded-lg hover:bg-muted transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Back</span>
          </button>

          <div className="flex items-center gap-2 px-3 py-2 bg-surface/90 backdrop-blur border border-input rounded-lg">
            <div className="w-2 h-2 rounded-full bg-brand-accent animate-pulse" />
            <span className="text-sm font-medium">TORQ Fleet Map</span>
            <span className="text-xs text-muted-foreground ml-1">
              {drivers.length} active driver{drivers.length !== 1 ? 's' : ''}
            </span>
          </div>

          {selectedDriver && (
            <button
              onClick={handleShowAll}
              className="flex items-center gap-2 px-3 py-2 bg-brand-accent/20 border border-brand-accent/40 rounded-lg hover:bg-brand-accent/30 transition-colors"
            >
              <Users className="w-4 h-4 text-brand-accent" />
              <span className="text-sm font-medium text-brand-accent">Show All Drivers</span>
              <X className="w-3 h-3 text-brand-accent/70" />
            </button>
          )}

          {/* Sidebar toggle when collapsed */}
          {!sidebarOpen && (
            <button
              onClick={() => setSidebarOpen(true)}
              className="ml-auto flex items-center gap-2 px-3 py-2 bg-surface/90 backdrop-blur border border-input rounded-lg hover:bg-muted transition-colors"
            >
              <Users className="w-4 h-4" />
              <span className="text-sm">Drivers</span>
            </button>
          )}
        </div>

        {/* Map */}
        {loading ? (
          <div className="h-full w-full flex items-center justify-center bg-surface">
            <div className="flex flex-col items-center gap-3">
              <div className="w-10 h-10 border-2 border-brand-accent border-t-transparent rounded-full animate-spin" />
              <span className="text-sm text-muted-foreground">Loading fleet data...</span>
            </div>
          </div>
        ) : (
          <MapView
            markers={markers}
            loads={routeLines}
            dark={true}
            showLegend={true}
            showSatelliteToggle={true}
          />
        )}
      </div>

      {/* Right sidebar - driver list */}
      {sidebarOpen && (
        <div className="w-80 border-l border-border bg-surface flex flex-col overflow-hidden shrink-0">
          {/* Sidebar header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-brand-accent" />
              <span className="text-sm font-semibold">Active Drivers</span>
              <span className="text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded">
                {drivers.length}
              </span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-1 hover:bg-muted rounded transition-colors"
            >
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>

          {/* Driver list */}
          <div className="flex-1 overflow-y-auto">
            {drivers.length === 0 && !loading && (
              <div className="p-6 text-center text-muted-foreground text-sm">
                No active drivers found
              </div>
            )}

            {drivers.map(driver => {
              const isSelected = selectedDriverId === driver.user_id;
              const activeLoads = driver.active_loads || [];
              const initial = (driver.name || 'D').charAt(0).toUpperCase();

              return (
                <button
                  key={driver.user_id}
                  onClick={() => handleSelectDriver(driver.user_id)}
                  className={`w-full text-left p-3 border-b border-border/50 hover:bg-muted/50 transition-colors ${
                    isSelected ? 'bg-brand-accent/10 border-l-2 border-l-brand-accent' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {/* Avatar initial */}
                    <div className="w-9 h-9 rounded-full bg-brand-accent/20 flex items-center justify-center shrink-0">
                      <span className="text-sm font-bold text-brand-accent">{initial}</span>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium truncate">{driver.name}</span>
                        <ChevronRight className={`w-3.5 h-3.5 text-muted-foreground shrink-0 transition-transform ${
                          isSelected ? 'rotate-90 text-brand-accent' : ''
                        }`} />
                      </div>

                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-muted-foreground">
                          {activeLoads.length} active load{activeLoads.length !== 1 ? 's' : ''}
                        </span>
                        {driver.last_location?.lat && (
                          <div className="flex items-center gap-0.5">
                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                            <span className="text-xs text-emerald-400">GPS</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Route sequence for active loads */}
                  {isSelected && activeLoads.length > 0 && (
                    <div className="mt-3 ml-12 space-y-2">
                      {activeLoads.map(load => (
                        <div key={load.load_id} className="text-xs space-y-1">
                          <div className="text-muted-foreground font-medium truncate">
                            {load.cargo_description || `Load #${load.load_id}`}
                          </div>
                          <div className="flex items-center gap-1.5">
                            <MapPin className="w-3 h-3 text-red-400 shrink-0" />
                            <span className="text-muted-foreground truncate">
                              {load.pickup_name || 'Pickup'}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 ml-1">
                            <div className="w-0.5 h-3 bg-zinc-700 ml-[4px]" />
                          </div>
                          <div className="flex items-center gap-1.5">
                            <MapPin className="w-3 h-3 text-emerald-400 shrink-0" />
                            <span className="text-muted-foreground truncate">
                              {load.delivery_name || 'Delivery'}
                            </span>
                          </div>
                          {load.status && (
                            <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded bg-muted text-muted-foreground text-[10px] uppercase tracking-wider">
                              {load.status.replace(/_/g, ' ')}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
